import React, { useEffect } from "react";
import HeroBanner from "./components/heroBanner";
import { Box, Tabs, Tab, useMediaQuery, useTheme } from "@mui/material";
import ChatBotContainer from "../../components/common/chat/chatBotContainer.jsx";
import SectionCardsDisplay from "./components/SectionCardsDisplay.jsx";
import CommonSubsectionUI from "../Sections/components/commonSubsectionUI";
import ViewSubsectionCalender from "../Sections/components/viewSubsectionCalender";
import { useSelector } from "react-redux";
import { useAuthState } from "../../hooks/useAuth";
import formatLanguage from "../../utils/formatLanguage";
import apiClient from "../../services/apiClient";
import { useLocation, useNavigate } from "react-router-dom";

function Home() {
  const [tabValue, setTabValue] = React.useState("subjects");

  const { isAuthenticated, isLoading, isInitialized } = useAuthState();
  const theme = useTheme();
  const isMediumScreen = useMediaQuery(theme.breakpoints.down("md"));
  const navigate = useNavigate();
  const location = useLocation();
  const originPath = `${location.pathname}${location.search}${location.hash}`;

  const selectedLanguage = useSelector(
    (state) => state.language.selectedLanguage || "en"
  );
  const language = formatLanguage(selectedLanguage);
  const section = "Home";

  const fetchContentData = async ({ subsection, filter }) => {
    try {
      const response = await apiClient.post("/section_page/view_subsection/", {
        subsection,
        section,
        language,
        filter: {
          ...filter,
          section_filter: "Home",
        },
      });
      return response.data;
    } catch (error) {
      console.error(error);
      throw error;
    }
  };

  // Wait for auth initialization before showing content
  if (!isInitialized || isLoading) {
    return <div>Loading...</div>;
  }

  if (!isAuthenticated) {
    return <div>Please log in</div>;
  }

  return (
    <div>
      <HeroBanner />
      <Box
        sx={{
          width: "100%",
          maxWidth: 1440,
          px: { xs: 2, sm: 3, md: 4 },
          py: { xs: 2, md: 3 },
          margin: "0 auto",
          display: "flex",
          flexDirection: { xs: "column", lg: "row" },
          justifyContent: "flex-start",
          alignItems: "stretch",
          gap: { xs: 3, md: 4 },
        }}
      >
        {/* Left Side - ChatBotContainer */}
        <Box
          sx={{
            flexBasis: { xs: "100%", lg: "35%" },
            flexGrow: 1,
            display: "flex",
            justifyContent: "center",
            alignItems: "flex-start",
            width: "100%",
          }}
        >
          <ChatBotContainer
            fullScreen={false}
            onOpenFullPage={() =>
              navigate("/chatbot", {
                state: { fromPath: originPath },
              })
            }
          />
        </Box>

        {/* Right Side - Tabs for switching content */}
        <Box
          sx={{
            flexBasis: { xs: "100%", lg: "65%" },
            display: "flex",
            flexDirection: "column",
            justifyContent: "flex-start",
            alignItems: "stretch",
            minHeight: 0,
            width: "100%",
          }}
        >
          {/* Tabs header */}
          <Tabs
            value={tabValue}
            onChange={(event, newValue) => setTabValue(newValue)}
            indicatorColor="primary"
            textColor="primary"
            variant={isMediumScreen ? "scrollable" : "fullWidth"}
            scrollButtons={isMediumScreen ? "auto" : false}
          >
            <Tab
              label="Subjects"
              value="subjects"
              sx={{ fontSize: "1.1rem", fontWeight: "bold" }}
            />
            <Tab
              label="Government Resolutions"
              value="Government Resolutions"
              sx={{ fontSize: "1.1rem", fontWeight: "bold" }}
            />
            <Tab
              label="Calendar"
              value="calendar"
              sx={{ fontSize: "1.1rem", fontWeight: "bold" }}
            />
          </Tabs>

          {/* Tab content */}
          <Box
            sx={{
              mt: { xs: 2, md: 3 },
              flex: 1,
              overflowY: "auto",
              pb: { xs: 2, md: 0 },
            }}
          >
            {tabValue === "subjects" && (
              <SectionCardsDisplay
                language={language}
                section={section}
                fetchContentData={fetchContentData}
                subsection="Subjects"
              />
            )}
            {tabValue === "Government Resolutions" && (
              <CommonSubsectionUI
                language={language}
                section={section}
                fetchContentData={fetchContentData}
                subsection="GR"
              />
            )}
            {tabValue === "calendar" && (
              <ViewSubsectionCalender
                language={language}
                section={section}
                fetchContentData={fetchContentData}
                subsection="calendar"
              />
            )}
          </Box>
        </Box>
      </Box>
    </div>
  );
}

export default Home;
